import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  QueryList,
  ViewChild,
  ViewChildren
}                               from '@angular/core';
import { NgbActiveModal }       from '@ng-bootstrap/ng-bootstrap';

import { HaynesProSubDrawingModel }               from '../typings/haynes-pro.sub-drawing';
import { HaynesProSvgInteractionDirective }       from '../shared/haynes-pro.svg-interaction.directive';
import { HaynesProTechnicalDrawingCategoryModel } from '../typings/haynes-pro.technical-drawing.category';

@Component({
  selector   : 'wor-haynes-pro-technical-drawings-viewer',
  template: require('./haynes-pro.technical-drawings-viewer.component.html'),
  styles: [ require('./haynes-pro.technical-drawings-viewer.component.scss') ]
})
export class HaynesProTechnicalDrawingsViewerComponent extends HaynesProSvgInteractionDirective implements AfterViewInit {
  @Input() subDrawing : HaynesProSubDrawingModel;
  @Input() title      : string;

  @ViewChild('categoriesContainer', { static: false }) categoriesContainer : ElementRef<HTMLDivElement>;
  @ViewChildren('categoryRow') categoryRows                                : QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild('svgContainer', { static: false }) svgContainer               : ElementRef<HTMLDivElement>;

  expandedCategoryId : number = null;
  hoveredCategoryId  : number = null;
  loadingSvg                  = false;
  svgError                    = '';

  private readonly allowedMatchTags = [ 'g', 'path', 'polygon', 'polyline', 'rect', 'circle', 'ellipse', 'line', 'use' ];

  constructor (
    public activeModal: NgbActiveModal
  ) {
    super();
  }

  ngAfterViewInit () : void {
    setTimeout(() => {
      this.loadInteractiveSvg();
    });
  }

  get categories () : Array<HaynesProTechnicalDrawingCategoryModel> {
    return (this.subDrawing?.categories || []).filter(category => category?.in_diagram !== false);
  }

  private findSvgElementsForGenart ( genartId : number ) : Array<Element> {
    if (!this.svgElement) {
      return [];
    }

    const selectors = [
      `#genart-${genartId}`,
      `#ga-${genartId}`,
      `[data-genart-id="${genartId}"]`,
      `[id="genart-${genartId}"]`,
      `[id="ga-${genartId}"]`,
      `[id^="genart-${genartId}"]`,
      `[id^="ga-${genartId}"]`,
      `[id*="genart-${genartId}-"]`,
      `[id*="ga-${genartId}-"]`,
      `[class~="genart-${genartId}"]`,
      `[class~="ga-${genartId}"]`
    ];

    for (const selector of selectors) {
      const elements = Array.from(this.svgElement.querySelectorAll(selector))
      .filter(( element : Element ) => this.allowedMatchTags.includes(element.tagName.toLowerCase()));

      if (elements.length) {
        return Array.from(new Set(elements));
      }
    }

    return [];
  }

  private async fetchSvgAsText ( url : string ) : Promise<string> {
    const response = await fetch(url, { mode: 'cors' });

    if (!response.ok) {
      throw new Error(`Failed to fetch technical drawing (${response.status}).`);
    }

    return response.text();
  }

  private getSvgElements ( genartId : number ) : Array<Element> {
    if (!genartId) {
      return [];
    }

    if (!this.svgElementCache[genartId]) {
      this.svgElementCache[genartId] = this.findSvgElementsForGenart(genartId);
    }

    return this.svgElementCache[genartId];
  }

  protected getInteractiveItemIds () : Array<number> {
    return this.categories
    .map(( category : HaynesProTechnicalDrawingCategoryModel ) => category?.genart_id)
    .filter(( genartId : number ) => !!genartId);
  }

  protected getMatchedSvgElements ( itemId : number ) : Array<Element> {
    return this.getSvgElements(itemId);
  }

  protected getHoveredItemId () : number | null {
    return this.hoveredCategoryId;
  }

  protected setHoveredItemId ( itemId : number | null ) : void {
    this.hoveredCategoryId = itemId;
  }

  protected getSelectedItemId () : number | null {
    return this.expandedCategoryId;
  }

  protected handleResolvedSvgClick ( itemId : number ) : void {
    this.expandedCategoryId = this.expandedCategoryId === itemId
      ? null
      : itemId;

    this.applySvgState();
    this.scrollSelectedCategoryIntoView();
  }

  handleCategoryHover ( category : HaynesProTechnicalDrawingCategoryModel ) : void {
    this.hoveredCategoryId = category?.genart_id || null;
    this.applySvgState();
  }

  handleCategoryLeave ( category : HaynesProTechnicalDrawingCategoryModel ) : void {
    if (this.hoveredCategoryId === category?.genart_id) {
      this.hoveredCategoryId = null;
      this.applySvgState();
    }
  }

  isExpanded ( category : HaynesProTechnicalDrawingCategoryModel ) : boolean {
    return this.expandedCategoryId === category?.genart_id;
  }

  isHovered ( category : HaynesProTechnicalDrawingCategoryModel ) : boolean {
    return this.hoveredCategoryId === category?.genart_id;
  }

  isSelected ( category : HaynesProTechnicalDrawingCategoryModel ) : boolean {
    return this.expandedCategoryId === category?.genart_id;
  }

  private async loadInteractiveSvg () : Promise<void> {
    if (!this.subDrawing?.image_url || !this.svgContainer?.nativeElement) {
      return;
    }

    this.loadingSvg = true;
    this.svgError   = '';

    try {
      const svgText = await this.fetchSvgAsText(this.subDrawing.image_url);

      this.injectInlineSvg(svgText, this.svgContainer);
      this.applySvgState();
    } catch (err) {
      this.svgError = err?.message || 'Unable to load technical drawing.';
    } finally {
      this.loadingSvg = false;
    }
  }

  private scrollSelectedCategoryIntoView () : void {
    if (!this.expandedCategoryId || !this.categoriesContainer?.nativeElement) {
      return;
    }

    setTimeout(() => {
      const container = this.categoriesContainer?.nativeElement;

      if (!container) {
        return;
      }

      const selectedRow = this.categoryRows?.find(( row : ElementRef<HTMLDivElement> ) =>
        Number(row?.nativeElement?.dataset?.genartId) === this.expandedCategoryId
      )?.nativeElement;

      if (!selectedRow) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const rowRect       = selectedRow.getBoundingClientRect();
      const topPadding    = 0;

      const nextTop = container.scrollTop + (rowRect.top - containerRect.top) - topPadding;

      container.scrollTo({
        top     : Math.max(nextTop, 0),
        behavior: 'smooth'
      });
    });
  }

  toggleCategory ( category : HaynesProTechnicalDrawingCategoryModel ) : void {
    this.expandedCategoryId = this.expandedCategoryId === category.genart_id
      ? null
      : category.genart_id;

    this.applySvgState();
    this.scrollSelectedCategoryIntoView();
  }

  trackByCategory ( _index : number, category : HaynesProTechnicalDrawingCategoryModel ) : number {
    return category?.genart_id;
  }

  trackByTask ( _index : number, task : any ) : string {
    return task?.id;
  }
}